import React, { useContext } from 'react';
import { formatColor } from '@meitu/saas-colors';
import { VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';
import { type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { VectorPanelContext } from '../../context';
import { Tracker } from '../../utils';

const FALLBACK_FILL = '#000000ff';

export const FillColorPicker: React.FC = () => {
    const { layerId, recordId } = useContext(VectorPanelContext)!;

    const { $t } = useLocaleStore();

    const editor = useEditor();
    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as IVectorLayerProps;
        const { fill, fillGradient } = layerData;

        return {
            fill,
            fillGradient,
        };
    });

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const fill = props ? props.fill : FALLBACK_FILL;
    const fillGradient = props ? props.fillGradient : null;

    const color = fillGradient ? (formatColor(fillGradient) as string) : fill;

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openFillColorPicker({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const setFillColor = (color: string, options = { writeHistory: true }) => {
        editor.getInstance(VectorLayer).setFillColor(layerId, color, options);

        if (options.writeHistory) {
            Tracker.setFillColor({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    return (
        <div className="vector-panel-controller">
            <LabelAction title={$t((v) => v.panel.vectorPanel.color)} align>
                <ColorGroup
                    isGradient
                    popupTriggerSize={{ width: 78, height: 32 }}
                    color={color}
                    setColor={(value) => setFillColor(value, { writeHistory: false })}
                    setColorAfter={(value, _, { isBrandColor }) => {
                        setFillColor(value, { writeHistory: true });
                        if (isBrandColor) {
                            window._stat_track?.('hb_layers_func_use', {
                                func_id: '9_107',
                            });
                        }
                    }}
                    popupOverlayClassName="vector-panel-color-picker"
                    onOpenChange={handleColorPickerOpenChange}
                    onToggleBizTab={(bizTab) => {
                        if (bizTab === 'team') {
                            window._stat_track?.('hb_layers_func_click', {
                                func_id: '9_107',
                            });
                        }
                    }}
                />
            </LabelAction>
        </div>
    );
};
